import { Flex, Spinner } from '@cad-arc';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { MenuLoadingError } from '../common/components/menu-loading-error';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { ClearEventItem } from './clear-event-item';
import { CopyCallItem } from './copy-call-item';
import { EditEventItem } from './edit-event-item';
import { GenerateReportingEventNumberItem } from './generate-reporting-event-number-item';
import { HoldEventItem } from './hold-event-item';
import { ManageUnitsItem } from './manage-units-item';
import { ReleaseBackupItem } from './release-backup-item';
import { TowVehicleItem } from './tow-vehicle-item';
import { ViewOnMapItem } from './view-on-map-item';
import { ManageEventMergeItem } from './manage-event-merge-item';
import { ContextMenuResource } from '@mark43/cad-api';

export function EventContextMenu({
    eventId,
    isContextMenuOpenFromAMap = false,
}: {
    eventId: number;
    isContextMenuOpenFromAMap?: boolean;
}): React.ReactElement {
    const apiClient = useApiClient();

    const { data: eventContextMenuView, isInitialLoading, isError } = useQuery(
        ['eventContextMenuView', eventId],
        () => {
            return apiClient
                .request<ContextMenuResource.GetEventContextMenu>({
                    method: 'GET',
                    url: `context_menu/event/${eventId}/`,
                })
                .then((response) => {
                    return response.data.data;
                });
        }
    );

    if (isInitialLoading) {
        return (
            <Flex justifyContent="center" alignItems="center" w="100%" h="100%">
                <Spinner />
            </Flex>
        );
    }

    if (isError) {
        return <MenuLoadingError />;
    }

    if (!eventContextMenuView) {
        return <div />;
    }

    const { clickParams } = eventContextMenuView;

    return (
        <ErrorBoundary
            name="EVENT_CONTEXT_MENU"
            messageVerbNoun="opening the event menu"
            asToast={true}
        >
            <ManageUnitsItem
                contextMenuItemView={eventContextMenuView.manageUnitsContextMenuItemView}
                clickParams={clickParams}
            />
            <ReleaseBackupItem
                contextMenuItemView={eventContextMenuView.releaseBackupContextMenuItemView}
                clickParams={clickParams}
            />
            <EditEventItem
                contextMenuItemView={eventContextMenuView.editEventContextMenuItemView}
                clickParams={clickParams}
            />
            <GenerateReportingEventNumberItem
                contextMenuItemView={
                    eventContextMenuView.generateReportingEventNumberContextMenuItemView
                }
                clickParams={clickParams}
            />
            <HoldEventItem
                contextMenuItemView={eventContextMenuView.holdEventContextMenuItemView}
                clickParams={clickParams}
            />
            {!isContextMenuOpenFromAMap && (
                <ViewOnMapItem
                    contextMenuItemView={eventContextMenuView.viewOnMapContextMenuItemView}
                    clickParams={clickParams}
                />
            )}
            <ManageEventMergeItem
                contextMenuItemView={eventContextMenuView.manageEventMergeContextMenuItemView}
                clickParams={clickParams}
            />
            <ClearEventItem
                contextMenuItemView={eventContextMenuView.clearEventContextMenuItemView}
                clickParams={clickParams}
            />
            <TowVehicleItem
                contextMenuItemView={eventContextMenuView.towVehicleContextMenuItemView}
                clickParams={clickParams}
            />
            <CopyCallItem
                contextMenuItemView={eventContextMenuView.copyCallContextMenuItemView}
                clickParams={clickParams}
            />
        </ErrorBoundary>
    );
}
